import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useAuth } from "../../contexts/AuthContext";
import LoadingScreen from "../../components/LoadingScreen/LoadingScreen";
import axiosInstance from "../../axiosInstance";
import AccessDenied from "../AccessDenied";
import "./MasterModule.scss";

/**
 * Reusable Master Module Component
 * Handles sidebar, progress tracking, and module rendering for CRM, Inventory, and ERP
 */
const MasterModule = ({ title, modules, apiEndpoints, getApiUrl }) => {
  const { user, isAuthorized } = useAuth();
  const userRoles = useMemo(() => user?.authorities || [], [user]);

  // Filter modules to only those the user can access
  const accessibleModules = useMemo(
    () =>
      modules.filter((mod) =>
        isAuthorized({ feature: mod.authConfig.feature, requireAll: false }),
      ),
    [modules, isAuthorized],
  );

  const [selectedPage, setSelectedPage] = useState(
    () => accessibleModules[0]?.name ?? null, // auto-select first accessible
  );

  const [completedPages, setCompletedPages] = useState(
    modules.reduce((acc, mod) => ({ ...acc, [mod.name]: false }), {}),
  );
  const [sidebarVisible, setSidebarVisible] = useState(true);
  const [loading, setLoading] = useState(true);
  const [apiDataFetched, setApiDataFetched] = useState(false);

  const defaultGetApiUrl = useCallback(
    (endpoint) => {
      if (userRoles.includes("ROLE_ERP-ADMIN")) {
        return `${process.env.REACT_APP_API_ERP_ADMIN_URL}${endpoint}`;
      } else if (userRoles.includes("ROLE_INVENTORY-MANAGER")) {
        return `${process.env.REACT_APP_API_INV_MANAGER_URL}${endpoint}`;
      } else if (userRoles.includes("ROLE_CRM-ADMIN")) {
        return `${process.env.REACT_APP_API_CRM_ADMIN_URL}${endpoint}`;
      }
      return `${process.env.REACT_APP_API_BASE_URL}${endpoint}`;
    },
    [userRoles],
  );

  const urlBuilder = getApiUrl || defaultGetApiUrl;

  const totalPages = accessibleModules.length;
  const completedCount = accessibleModules.filter(
    (mod) => completedPages[mod.name],
  ).length;
  const progress = totalPages > 0 ? (completedCount / totalPages) * 100 : 0;

  const markPageAsCompleted = useCallback((page) => {
    setCompletedPages((prev) => ({ ...prev, [page]: true }));
  }, []);

  const fetchModuleData = useCallback(
    async (moduleName, endpoint, authFeature) => {
      if (!isAuthorized({ feature: authFeature })) return;

      try {
        if (Array.isArray(endpoint)) {
          const responses = await Promise.all(
            endpoint.map((ep) => axiosInstance.get(urlBuilder(ep))),
          );
          const hasData = responses.some(
            (res) => Array.isArray(res.data?.data) && res.data.data.length > 0,
          );
          if (hasData) markPageAsCompleted(moduleName);
        } else {
          const response = await axiosInstance.get(urlBuilder(endpoint));
          if (
            Array.isArray(response.data?.data) &&
            response.data.data.length > 0
          ) {
            markPageAsCompleted(moduleName);
          }
        }
      } catch (error) {
        console.error(`Error fetching ${moduleName}:`, error);
      }
    },
    [markPageAsCompleted, urlBuilder, isAuthorized],
  );

  const fetchAllApiData = useCallback(async () => {
    if (apiDataFetched) return;
    setLoading(true);
    try {
      await Promise.all(
        // Only fetch data for accessible modules
        accessibleModules.map((mod) => {
          const endpoint = apiEndpoints[mod.name];
          if (!endpoint) return Promise.resolve();
          return fetchModuleData(mod.name, endpoint, mod.authConfig.feature);
        }),
      );
      setApiDataFetched(true);
    } catch (error) {
      console.error(`Error fetching ${title} data:`, error);
    } finally {
      setLoading(false);
    }
  }, [apiDataFetched, accessibleModules, apiEndpoints, fetchModuleData, title]);

  useEffect(() => {
    fetchAllApiData();
  }, [fetchAllApiData]);

  if (loading) return <LoadingScreen />;

  // If user has no access to any module at all
  if (accessibleModules.length === 0) return <AccessDenied />;

  const renderContent = () => {
    if (!selectedPage) return <AccessDenied />;
    const module = accessibleModules.find((m) => m.name === selectedPage);
    if (!module) return <AccessDenied />;
    return <module.component markPageAsCompleted={markPageAsCompleted} />;
  };

  return (
    <div className="master-container">
      <div
        className={`sidebar-container ${sidebarVisible ? "visible" : "hidden"}`}
      >
        <p className="admin-heading">{title}</p>

        <div className="progress-bar-container">
          <div className="progress-header">
            <h3>Setup Progress</h3>
            <span className="progress-percentage">{Math.round(progress)}%</span>
          </div>
          <div className="progress-bar-track">
            <div
              className="progress-bar"
              style={{ width: `${progress}%` }}
            ></div>
          </div>
          <div className="progress-stats">
            <span className="completed-modules">
              {completedCount} modules configured
            </span>
            <span className="remaining-modules">
              {totalPages - completedCount} remaining
            </span>
          </div>

          {progress < 100 && (
            <p className="demo-note text-xs text-gray-500 mt-2 italic">
              Note: Sample data has been pre-filled to help you get started —
              setup progress reflects demo configuration.
            </p>
          )}

          {progress === 100 && (
            <div className="completion-success">
              <div className="success-icon">✅</div>
              <div className="success-text">
                <p>
                  <strong>Setup Complete!</strong>
                </p>
                <p>Your system is fully configured and ready to use</p>
              </div>
            </div>
          )}
        </div>

        <p className="scroll-instruction">{">>"}</p>

        <ul className="sidebar-menu">
          {modules.map((mod) => {
            const hasAccess = isAuthorized({
              feature: mod.authConfig.feature,
              requireAll: false,
            });

            return (
              <li
                key={mod.name}
                className={`sidebar-item ${selectedPage === mod.name ? "active" : ""} ${
                  completedPages[mod.name] ? "completed" : ""
                } ${!hasAccess ? "locked" : ""}`}
                onClick={() => hasAccess && setSelectedPage(mod.name)}
                title={!hasAccess ? "You don't have access to this module" : ""}
              >
                <div className="sidebar-item-content">
                  <span
                    className={`status-dot ${
                      !hasAccess
                        ? "locked"
                        : completedPages[mod.name]
                          ? "completed"
                          : "incomplete"
                    }`}
                  ></span>
                  {mod.name}
                  {!hasAccess && <span className="lock-icon">🔒</span>}
                </div>
              </li>
            );
          })}
        </ul>
      </div>

      <button
        className="sidebar-toggle-btn"
        onClick={() => setSidebarVisible(!sidebarVisible)}
      >
        &#8230;
      </button>

      <div
        className={`content-area ${sidebarVisible ? "sidebar-visible" : "sidebar-hidden"}`}
      >
        {renderContent()}
      </div>
    </div>
  );
};

export default MasterModule;
